import { match } from 'ts-pattern';

import { reportEvent } from '~/domains/analytics';
import type { BonusDefinition, NormalizedBonus } from '~/domains/promotions/types/bonus.types';

import type { PublicBoost } from '../../contest/domains/services/interfaces/propsService/api/boosts';
import { PromotionAnalyticsType } from '../constants/analytics-enums';
import type { PromotionActionLocation } from '../constants/analytics-enums';
import { VoucherType } from '../types/enums';
import type { VoucherResponseDto } from '../types/voucher.types';

type PromotionPayload = {
  promotionType: PromotionAnalyticsType;
  promotionId: string;
  promotionName: string;
  promotionDescription: string;
  promotionBoostType?: string;
  promotionBoostPayoutType?: string;
  promotionPct?: number;
  promotionUSD?: number;
  promotionVoucherType?: string;
  promotionBonusAmount?: number;
  promotionMinDeposit?: number;
};

export const constructPromotionPayload = (
  promotion: PublicBoost | VoucherResponseDto | NormalizedBonus,
  props: Record<string, unknown> = {}
): PromotionPayload | Record<string, never> => {
  if (!promotion) return {};

  // Determine promotion type for all promotion types using match pattern
  const promotionType = match(promotion)
    .returnType<PromotionAnalyticsType>()
    // Deposit Bonuses - match on discriminator property
    .with({ promotionType: 'deposit_bonus' }, () => PromotionAnalyticsType.DEPOSIT_BONUS)
    // QP Boosts
    .with({ type: 'payout_boost' }, () => PromotionAnalyticsType.QP_BOOST)
    .with({ type: 'risk_free' }, () => PromotionAnalyticsType.QP_BOOST)
    // QP Vouchers
    .with({ voucherType: VoucherType.QUICKPICKS }, () => PromotionAnalyticsType.QP_VOUCHER)
    // Contest Vouchers
    .with(
      { voucherType: VoucherType.CONTEST_ID_RESTRICTED },
      () => PromotionAnalyticsType.CONTEST_VOUCHER
    )
    .with({ voucherType: VoucherType.CONTESTS }, () => PromotionAnalyticsType.CONTEST_VOUCHER)
    .with({ voucherType: VoucherType.LEAGUES }, () => PromotionAnalyticsType.CONTEST_VOUCHER)
    // General Vouchers
    .with({ voucherType: VoucherType.GENERAL }, () => PromotionAnalyticsType.GENERAL_VOUCHER)
    .otherwise(() => PromotionAnalyticsType.GENERAL_VOUCHER);

  // Base payload first - common fields for all promotion types
  const basePayload = {
    promotionType,
    promotionId: promotion.id,
    promotionName: promotion.title,
    promotionDescription: promotion.description,
    ...props,
  };

  // Type-specific payload extensions
  if (promotionType === PromotionAnalyticsType.QP_BOOST) {
    const boost = promotion as PublicBoost;
    return {
      ...basePayload,
      promotionBoostType: boost.type,
      promotionBoostPayoutType: boost.payout_type,
      ...(boost.payout_type === 'percentage'
        ? {
            promotionPct: boost.amount * 100,
          }
        : {
            promotionUSD: boost.amount,
          }),
    };
  }

  if (promotionType === PromotionAnalyticsType.DEPOSIT_BONUS) {
    const bonus = promotion as NormalizedBonus;
    return {
      ...basePayload,
      promotionBonusAmount: bonus.maxBonusCents / 100, // Convert cents to dollars
      promotionMinDeposit: bonus.minDepositCents / 100, // Convert cents to dollars
      promotionPct: bonus.isPercentage ? bonus.percentage : undefined,
      promotionUSD: !bonus.isPercentage ? bonus.maxBonusCents / 100 : undefined,
    };
  }

  // Voucher payload (default case)
  const voucher = promotion as VoucherResponseDto;
  return {
    ...basePayload,
    promotionUSD: voucher.maxValue / 100,
    promotionVoucherType: voucher.voucherType,
  };
};

// Interacting with promotions (boosts, vouchers, deposit bonuses)
type ReportProps = {
  location: PromotionActionLocation;
  promotion: PublicBoost | VoucherResponseDto | NormalizedBonus;
  props?: Record<string, unknown>;
};
export const reportPromotionApply = ({ location, promotion, props }: ReportProps) =>
  reportEvent('Promotion > Apply', {
    location,
    ...constructPromotionPayload(promotion, props),
  });

export const reportPromotionSwap = ({ location, promotion, props }: ReportProps) =>
  reportEvent('Promotion > Swap', {
    location,
    ...constructPromotionPayload(promotion, props),
  });

export const reportPromotionRemove = ({ location, promotion, props }: ReportProps) =>
  reportEvent('Promotion > Remove', {
    location,
    ...constructPromotionPayload(promotion, props),
  });

export const reportPromotionViewTerms = ({ location, promotion, props }: ReportProps) =>
  reportEvent('Promotion > View Terms', {
    location,
    ...constructPromotionPayload(promotion, props),
  });

// Interacting with promo codes
export const reportPromoCodeValidate = ({
    location,
    promoCode,
    isValid,
  }: {
    location: PromotionActionLocation,
    promoCode: string,
    isValid: boolean,
  }) =>
  reportEvent('Promotion > Validate Promo Code', {
    location,
    promoCode,
    isValid,
  });

export const reportPromoCodeClaim = (
  { location, promoCode }: { location: PromotionActionLocation, promoCode: string }
) =>
  reportEvent('Promotion > Claim Promo Code', {
    location,
    promoCode,
  });
